import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from '@/hooks/use-toast';
import { emailImportsApi, type EmailContactImport } from '../api';
import { Loader2, Plus, Download } from 'lucide-react';

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  COMPLETED: 'default',
  COMPLETED_WITH_ERRORS: 'secondary',
  FAILED: 'destructive',
  CANCELLED: 'outline',
};

export default function EmailImportsAdminPage() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<EmailContactImport[]>([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await emailImportsApi.list(1, 50);
        setItems(res.items);
      } catch (error: any) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error?.response?.data?.message || 'No se pudo cargar el historial.',
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const downloadErrors = async (imp: EmailContactImport) => {
    try {
      const blob = await emailImportsApi.downloadErrors(imp.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `import-${imp.id}-errors.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'No se pudo descargar el reporte.' });
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold">Importaciones de Contactos</h1>
            <p className="text-muted-foreground">Historial de importaciones CSV.</p>
          </div>
          <Link to="/admin/email-contacts/imports/new">
            <Button>
              <Plus className="h-4 w-4 mr-2" /> Importar contactos
            </Button>
          </Link>
        </div>

        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Archivo</TableHead>
                <TableHead>Origen</TableHead>
                <TableHead>Líneas</TableHead>
                <TableHead>Nuevos</TableHead>
                <TableHead>Existentes</TableHead>
                <TableHead>Inválidos</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Cargando...
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                    No hay importaciones registradas.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((imp) => (
                  <TableRow key={imp.id}>
                    <TableCell>#{imp.id}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{imp.filename}</TableCell>
                    <TableCell>{imp.sourceType}</TableCell>
                    <TableCell>{imp.totalRows}</TableCell>
                    <TableCell>{imp.newContacts}</TableCell>
                    <TableCell>{imp.existingContacts}</TableCell>
                    <TableCell>{imp.invalidRows}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[imp.status] || 'outline'}>{imp.status}</Badge>
                    </TableCell>
                    <TableCell>{new Date(imp.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {imp.invalidRows > 0 && (
                        <Button variant="ghost" size="sm" onClick={() => downloadErrors(imp)}>
                          <Download className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </Layout>
  );
}
